/**
 * Megan's circular emblem, derived from the client-supplied banner artwork
 * (design/logo/source/YL_banner.png) by alpha-keying the white line art off
 * its sage background and refilling it with a single brand color. Two
 * variants, both transparent, so the mark sits correctly on cream and on ink
 * without the rectangular edge of the original JPEG.
 *
 * Decorative by default: the adjacent <Wordmark> already carries the
 * accessible name, so the mark is aria-hidden to avoid a duplicate label.
 * See design/logo/README.md for the derivation commands.
 *
 * The emblem is dense line art (rays, pines, a winding path) and it goes soft
 * when the browser downscales one large raster to ~44px. So each size is
 * pre-rendered with Lanczos plus a light unsharp, and picked via srcset. The
 * `sizes` value tracks the rendered widths in BrandLockup: 36/44px in the
 * header, 44/56px in the footer.
 */

type Variant = 'ink' | 'cream';

interface Props {
  variant?: Variant;
  className?: string;
}

const WIDTHS = [64, 128, 192, 256];

export function BrandMark({ variant = 'ink', className = '' }: Props) {
  const base = `/brand/yl-mark-${variant === 'cream' ? 'cream' : 'ink'}`;
  const sizes = '(min-width: 640px) 56px, 44px';
  const srcSet = (ext: string) =>
    WIDTHS.map((w) => `${base}-${w}.${ext} ${w}w`).join(', ');

  return (
    <picture>
      <source
        type="image/webp"
        srcSet={srcSet('webp')}
        sizes={sizes}
      />
      <img
        src={`${base}-128.png`}
        srcSet={srcSet('png')}
        sizes={sizes}
        alt=""
        aria-hidden
        width={256}
        height={256}
        decoding="async"
        className={`block h-auto ${className}`}
      />
    </picture>
  );
}
